import {
  CustomLayerInterface,
  CustomRenderMethodInput,
  Map as MlMap,
  MercatorCoordinate,
  RasterSourceSpecification,
  LngLat,
} from "maplibre-gl";
import { loadAeroTile } from "./webgl-maplibre.aero-tiles";

type GL = WebGLRenderingContext | WebGL2RenderingContext;
const TERRAIN_CLEARANCE_M = 300 * 0.3048;
const HEIGHT_CHANGE_THRESHOLD_M = 500 * 0.3048;
const planeHeights = new WeakMap<MlMap, number>();
export function getAeroPlaneHeight(map: MlMap): number {
  return planeHeights.get(map) ?? TERRAIN_CLEARANCE_M;
}
interface Tile {
  x: number;
  y: number;
  z: number;
  texture?: WebGLTexture;
}

/** One horizontal sheet for the entire viewport, never one height per tile. */
export function aeroPlaneAltitude(centerElevation: number | null): number | null {
  return centerElevation !== null && Number.isFinite(centerElevation) ? centerElevation + TERRAIN_CLEARANCE_M : null;
}

/** A coarse screen grid reads only already-loaded DEM data, never fetching tiles. */
export function lowestTerrainInView(map: MlMap): number | null {
  const canvas = map.getCanvas();
  const elevations: number[] = [];
  for (const y of [0.1, 0.5, 0.9])
    for (const x of [0.1, 0.5, 0.9]) {
      const elevation = map.queryTerrainElevation(map.unproject([canvas.clientWidth * x, canvas.clientHeight * y]));
      if (elevation !== null && Number.isFinite(elevation)) elevations.push(elevation);
    }
  return elevations.length ? Math.min(...elevations) : null;
}

export class AeroPlaneLayer implements CustomLayerInterface {
  readonly id = "aero-plane";
  readonly type = "custom" as const;
  readonly renderingMode = "3d" as const;
  private map: MlMap;
  private gl: GL;
  private program: WebGLProgram;
  private buffer: WebGLBuffer;
  private tiles = new Map<string, Tile>();
  private controller = new AbortController();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshBusy = false;
  private refreshQueued = false;
  private refreshStartedAt = -Infinity;
  private removed = false;
  private height = TERRAIN_CLEARANCE_M;
  private heightUpdatedAt = -Infinity;
  private heightTimer: ReturnType<typeof setTimeout> | null = null;
  private panView: { center: LngLat; zoom: number; bearing: number; pitch: number } | null = null;
  private source: RasterSourceSpecification;
  private minZoom = 0;
  private maxZoom = 24;

  onAdd(map: MlMap, gl: GL): void {
    this.map = map;
    this.gl = gl;
    this.source = map.getSource("aero-tiles").serialize() as RasterSourceSpecification;
    const layer = map.getStyle().layers.find((l) => l.id === "aero-layer");
    this.minZoom = layer.minzoom ?? 0;
    this.maxZoom = layer.maxzoom ?? 24;
    const shaders = [
      this.shader(
        gl.VERTEX_SHADER,
        `attribute vec2 a_uv;
        uniform mat4 u_matrix; uniform vec4 u_tile; uniform float u_height;
        varying vec2 v_uv;
        void main() { v_uv = a_uv; gl_Position = u_matrix * vec4(u_tile.xy + a_uv * u_tile.z, u_height, 1.0); }`
      ),
      this.shader(
        gl.FRAGMENT_SHADER,
        `precision mediump float;
        uniform sampler2D u_image; varying vec2 v_uv;
        void main() { gl_FragColor = texture2D(u_image, v_uv); }`
      ),
    ];
    this.program = gl.createProgram();
    shaders.forEach((shader) => gl.attachShader(this.program, shader));
    gl.linkProgram(this.program);
    shaders.forEach((shader) => gl.deleteShader(shader));
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(this.program));
    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    map.on("moveend", this.refresh);
    map.on("move", this.checkPanHeight);
    map.on("idle", this.initializeHeight);
    map.on("resize", this.resetPanHeight);
    this.refresh();
  }

  private shader(type: number, source: string): WebGLShader {
    const shader = this.gl.createShader(type);
    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) throw new Error(this.gl.getShaderInfoLog(shader));
    return shader;
  }

  private currentPanView() {
    const center = this.map.getCenter();
    return {
      center: new LngLat(center.lng, center.lat),
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
    };
  }

  private resetPanHeight = (): void => {
    this.panView = this.currentPanView();
    if (this.heightTimer !== null) clearTimeout(this.heightTimer);
    this.heightTimer = null;
  };

  private initializeHeight = (): void => {
    // One initial lookup when terrain has loaded. Subsequent lookups are pan-only.
    this.map.off("idle", this.initializeHeight);
    this.resetPanHeight();
    this.refreshHeight();
  };

  private checkPanHeight = (): void => {
    if (!this.panView) return; // Wait for the initial terrain load.
    const view = this.currentPanView();
    if (
      this.map.isZooming() ||
      view.zoom !== this.panView.zoom ||
      view.bearing !== this.panView.bearing ||
      view.pitch !== this.panView.pitch
    ) {
      this.resetPanHeight();
      return;
    }
    // Project both anchors on the same flat plane: no terrain reads here.
    const previous = this.map._camera.transform.locationToScreenPoint(this.panView.center);
    const current = this.map._camera.transform.locationToScreenPoint(view.center);
    const canvas = this.map.getCanvas();
    if (!canvas.clientWidth || !canvas.clientHeight) return;
    const distance = Math.hypot(
      (previous.x - current.x) / canvas.clientWidth,
      (previous.y - current.y) / canvas.clientHeight
    );
    if (!Number.isFinite(distance) || distance < 1) return;
    const previousHeight = this.height;
    this.refreshHeight();
    if (this.height !== previousHeight) this.panView = view;
  };

  private refreshHeight = (): void => {
    const map = this.map;
    const remaining = 1000 - (performance.now() - this.heightUpdatedAt);
    if (remaining > 0) {
      if (this.heightTimer === null)
        this.heightTimer = setTimeout(() => {
          this.heightTimer = null;
          if (this.panView) this.checkPanHeight();
          else this.refreshHeight();
        }, remaining);
      return;
    }
    if (this.heightTimer !== null) clearTimeout(this.heightTimer);
    this.heightTimer = null;
    this.heightUpdatedAt = performance.now();
    // Coarse minimum over the current view; no extra DEM fetches or decodes.
    const height = aeroPlaneAltitude(lowestTerrainInView(map));
    if (height === null) return;
    // Initialize from the first valid sample, then compare with the applied
    // height (not the last sample), so small changes can accumulate.
    if (!planeHeights.has(map) || Math.abs(height - this.height) > HEIGHT_CHANGE_THRESHOLD_M + 1e-9) {
      this.height = height;
      planeHeights.set(map, height);
      map.triggerRepaint();
    }
  };

  private refresh = (): void => {
    if (this.removed) return;
    if (this.refreshBusy) {
      this.refreshQueued = true;
      return;
    }
    if (this.refreshTimer !== null) return;
    const delay = Math.max(0, 2000 - (performance.now() - this.refreshStartedAt));
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (this.removed) return;
      this.refreshBusy = true;
      this.refreshQueued = false;
      this.refreshStartedAt = performance.now();
      void this.refreshTiles()
        .catch((error) => {
          if (!this.removed) console.warn("[aero-plane] refresh failed", error);
        })
        .finally(() => {
          this.refreshBusy = false;
          if (this.refreshQueued) this.refresh();
        });
    }, delay);
  };

  private refreshTiles = async (): Promise<void> => {
    this.controller.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const map = this.map;
    if (map.getZoom() < this.minZoom || map.getZoom() >= this.maxZoom) return;
    const bounds = map.getBounds();
    const coverage = this.source.bounds ?? [-180, -85.051129, 180, 85.051129];
    const west = Math.max(bounds.getWest(), coverage[0]);
    const east = Math.min(bounds.getEast(), coverage[2]);
    const south = Math.max(bounds.getSouth(), coverage[1]);
    const north = Math.min(bounds.getNorth(), coverage[3]);
    const nw = MercatorCoordinate.fromLngLat([west, north]);
    const se = MercatorCoordinate.fromLngLat([east, south]);
    let z = Math.max(this.source.minzoom ?? 0, Math.min(this.source.maxzoom ?? 22, Math.floor(map.getZoom()) + 1));
    // Bound GPU memory and requests in high-pitch views with a distant horizon.
    const tileCount = () =>
      (Math.floor(se.x * 2 ** z) - Math.floor(nw.x * 2 ** z) + 1) *
      (Math.floor(se.y * 2 ** z) - Math.floor(nw.y * 2 ** z) + 1);
    while (z > (this.source.minzoom ?? 0) && tileCount() > 128) z--;
    const wanted = new Map<string, Tile>();
    const n = 2 ** z;
    if (west < east && south < north && tileCount() <= 128) {
      for (let x = Math.max(0, Math.floor(nw.x * n)); x <= Math.min(n - 1, Math.floor(se.x * n)); x++) {
        for (let y = Math.max(0, Math.floor(nw.y * n)); y <= Math.min(n - 1, Math.floor(se.y * n)); y++) {
          const key = `${z}/${x}/${y}`;
          wanted.set(key, this.tiles.get(key) ?? { x, y, z });
        }
      }
    }
    for (const [key, tile] of this.tiles) if (!wanted.has(key) && tile.texture) this.gl.deleteTexture(tile.texture);
    this.tiles = wanted;
    map.setLayoutProperty("aero-layer", "visibility", "none");
    const pending = [...wanted.values()].filter((tile) => !tile.texture);
    await Promise.all(
      Array.from({ length: Math.min(6, pending.length) }, async () => {
        while (pending.length && !signal.aborted) {
          const tile = pending.shift();
          try {
            const url = this.source.tiles[0]
              .replace(/^efb-aero:\/\//, "")
              .replace("{z}", String(tile.z))
              .replace("{x}", String(tile.x))
              .replace("{y}", String(tile.y));
            const { data } = await loadAeroTile(url, signal);
            const image = await createImageBitmap(new Blob([data], { type: "image/png" }), {
              premultiplyAlpha: "none",
            });
            if (signal.aborted) {
              image.close();
              return;
            }
            const gl = this.gl;
            const previous = gl.getParameter(gl.TEXTURE_BINDING_2D);
            tile.texture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D, tile.texture);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            gl.bindTexture(gl.TEXTURE_2D, previous);
            image.close();
            map.triggerRepaint();
          } catch (error) {
            if (!signal.aborted) console.warn("[aero-plane] tile load failed", error);
          }
        }
      })
    );
    map.triggerRepaint();
  };

  render(gl: GL, input: CustomRenderMethodInput): void {
    if (this.height === null || this.map.getZoom() < this.minZoom || this.map.getZoom() >= this.maxZoom) return;
    // No save and restore of GL state: MapLibre's drawCustom marks all of it
    // dirty after render() and sets it again. Each getParameter or isEnabled
    // is a synchronous GPU round trip, and this runs on every frame.
    gl.activeTexture(gl.TEXTURE0);
    // Labels stay legible even where a peak protrudes through the average-height plane.
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.depthMask(false);
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.program);
    // Translate on the CPU before converting to Float32, avoiding jitter at close zoom.
    const origin = MercatorCoordinate.fromLngLat(this.map.getCenter());
    const raw = input.defaultProjectionData.mainMatrix;
    const matrix = new Float32Array(16);
    for (let i = 0; i < 16; i++) matrix[i] = raw[i];
    for (let i = 0; i < 4; i++) matrix[12 + i] = raw[12 + i] + raw[i] * origin.x + raw[4 + i] * origin.y;
    gl.uniformMatrix4fv(gl.getUniformLocation(this.program, "u_matrix"), false, matrix);
    // A single Mercator z also keeps the sheet mathematically planar across tile boundaries.
    gl.uniform1f(
      gl.getUniformLocation(this.program, "u_height"),
      MercatorCoordinate.fromLngLat(this.map.getCenter(), this.height).z
    );
    gl.uniform1i(gl.getUniformLocation(this.program, "u_image"), 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    const uv = gl.getAttribLocation(this.program, "a_uv");
    gl.enableVertexAttribArray(uv);
    gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 0, 0);
    for (const tile of this.tiles.values()) {
      if (!tile.texture) continue;
      const size = 1 / 2 ** tile.z;
      gl.uniform4f(
        gl.getUniformLocation(this.program, "u_tile"),
        tile.x * size - origin.x,
        tile.y * size - origin.y,
        size,
        0
      );
      gl.bindTexture(gl.TEXTURE_2D, tile.texture);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    gl.disableVertexAttribArray(uv);
  }

  onRemove(): void {
    this.removed = true;
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (this.heightTimer !== null) clearTimeout(this.heightTimer);
    this.heightTimer = null;
    planeHeights.delete(this.map);
    this.controller.abort();
    this.map.off("moveend", this.refresh);
    this.map.off("move", this.checkPanHeight);
    this.map.off("idle", this.initializeHeight);
    this.map.off("resize", this.resetPanHeight);
    for (const tile of this.tiles.values()) if (tile.texture) this.gl.deleteTexture(tile.texture);
    this.tiles.clear();
    this.gl.deleteBuffer(this.buffer);
    this.gl.deleteProgram(this.program);
  }
}

export function showAeroPlane(map: MlMap): void {
  if (map.getTerrain() && map.getLayer("aero-layer") && !map.getLayer("aero-plane")) map.addLayer(new AeroPlaneLayer());
}

export function hideAeroPlane(map: MlMap): void {
  if (map.getLayer("aero-plane")) map.removeLayer("aero-plane");
  if (map.getLayer("aero-layer")) map.setLayoutProperty("aero-layer", "visibility", "visible");
}
